import { useState } from "react";
import { Link, NavLink, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import { api, formatErr, useFetch, fmtTime } from "@/lib/api";
import { Loading } from "@/components/common";
import { PRODUCT, SOLUTIONS, DOCS, LEGAL } from "@/pages/site/content";

const Shell = ({ eyebrow, title, intro, children }) => (
  <div className="max-w-6xl mx-auto px-6 py-20">
    <div className="eyebrow mb-3">{eyebrow}</div>
    <h1 className="text-4xl sm:text-5xl font-black">{title}</h1>
    {intro && <p className="mt-4 text-gray-600 max-w-2xl">{intro}</p>}
    <div className="mt-12">{children}</div>
  </div>
);

export function ProductPage() {
  return (
    <Shell eyebrow="Product" title="Everything an IT administrator does first — automated." intro="CodeRed combines an endpoint agent, a rules and AI diagnostics engine and a controlled remediation engine.">
      <div className="grid md:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
        {PRODUCT.map(([t, d], i) => (
          <div key={t} className="bg-white p-8 fade-up" style={{ animationDelay: `${i * 0.04}s` }} data-testid={`product-feature-${i}`}>
            <span className="font-mono text-xs text-red-600">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="mt-2 text-lg font-bold">{t}</h3><p className="mt-2 text-sm text-gray-600">{d}</p>
          </div>
        ))}
      </div>
    </Shell>
  );
}

export function SolutionsPage() {
  const { slug = "small-business" } = useParams();
  const [title, text, points] = SOLUTIONS[slug] || SOLUTIONS["small-business"];
  return (
    <Shell eyebrow="Solutions" title={title} intro={text}>
      <div className="grid md:grid-cols-4 gap-10">
        <nav className="flex md:flex-col gap-2 flex-wrap">
          {Object.entries(SOLUTIONS).map(([k, [t]]) => (
            <NavLink key={k} to={`/solutions/${k}`} data-testid={`solution-tab-${k}`} className={({ isActive }) => `text-sm px-3 py-2 border-l-2 ${isActive ? "border-red-600 font-semibold" : "border-transparent text-gray-500 hover:text-gray-900"}`}>{t}</NavLink>
          ))}
        </nav>
        <div className="md:col-span-3 space-y-4">
          {points.map((p) => <div key={p} className="card-flat p-5 flex gap-3"><CheckCircle2 className="w-5 h-5 text-green-600" />{p}</div>)}
          <Link to="/register" className="inline-block mt-4 px-6 py-3 bg-red-600 text-white font-semibold hover:bg-red-700" data-testid="solution-cta-btn">Start 15-Day Trial</Link>
        </div>
      </div>
    </Shell>
  );
}

export function DocsPage() {
  const { slug = "getting-started" } = useParams();
  const [title, html] = DOCS[slug] || DOCS["getting-started"];
  return (
    <div className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-4 gap-10">
      <nav className="flex md:flex-col gap-1 flex-wrap" data-testid="docs-nav">
        <div className="eyebrow mb-2 w-full">Documentation</div>
        {Object.entries(DOCS).map(([k, [t]]) => (
          <NavLink key={k} to={`/docs/${k}`} data-testid={`docs-link-${k}`} className={({ isActive }) => `text-sm px-3 py-1.5 border-l-2 ${isActive ? "border-red-600 font-semibold" : "border-gray-200 text-gray-500 hover:text-gray-900"}`}>{t}</NavLink>
        ))}
      </nav>
      <article className="md:col-span-3 prose-doc" data-testid="docs-article">
        <h1 className="text-3xl md:text-4xl font-black mb-6">{title}</h1>
        <div dangerouslySetInnerHTML={{ __html: html.replaceAll("&lt;SERVER&gt;", process.env.REACT_APP_BACKEND_URL) }} />
      </article>
    </div>
  );
}

export function LegalPage() {
  const { slug = "privacy" } = useParams();
  const [title, html] = LEGAL[slug] || LEGAL.privacy;
  return <Shell eyebrow="Legal" title={title}><div className="prose-doc max-w-3xl" data-testid="legal-content" dangerouslySetInnerHTML={{ __html: html }} /></Shell>;
}

export function AboutPage() {
  return (
    <Shell eyebrow="About" title="Built for the IT work nobody should do twice." intro="CodeRed is an AI-powered IT management platform for businesses and managed service providers in the United States, United Kingdom, Australia, Canada and India.">
      <div className="grid md:grid-cols-3 gap-6">
        {[["Security first", "Automation is constrained by policy, signatures and human approval."], ["Honest by default", "We never present a simulated action or an unverified repair as a success."], ["Practical AI", "AI explains and recommends; deterministic playbooks act."]].map(([t, d]) => (
          <div key={t} className="card-flat p-6"><h3 className="font-bold">{t}</h3><p className="text-sm text-gray-600 mt-2">{d}</p></div>
        ))}
      </div>
    </Shell>
  );
}

export function ContactPage() {
  const [sp] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", company: "", topic: sp.get("topic") || "demo", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", f);
      setDone(true);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };
  const inp = "w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:border-red-600";
  return (
    <Shell eyebrow="Contact" title="Book a demo or talk to us." intro="Tell us about your environment and we'll get back to you.">
      <div className="mb-8 flex flex-col sm:flex-row gap-3 sm:gap-8 text-sm" data-testid="contact-emails">
        <a href="mailto:admin@dsouzainfotech.com" className="font-semibold hover:text-red-600" data-testid="contact-email-admin">admin@dsouzainfotech.com</a>
        <a href="mailto:support@centraldatastorage.com" className="font-semibold hover:text-red-600" data-testid="contact-email-support">support@centraldatastorage.com</a>
      </div>
      {done ? <div className="card-flat p-8 max-w-xl" data-testid="contact-success">Thanks — your message has been received.</div> : (
        <form onSubmit={submit} className="max-w-xl space-y-4" data-testid="contact-form">
          <input required placeholder="Name" className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name-input" />
          <input required type="email" placeholder="Work email" className={inp} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email-input" />
          <input placeholder="Company" className={inp} value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} data-testid="contact-company-input" />
          <select className={inp} value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} data-testid="contact-topic-select">
            <option value="demo">Book a demo</option><option value="sales">Sales / MSP pricing</option><option value="support">Support</option><option value="security">Security</option>
          </select>
          <textarea required minLength={5} rows={5} placeholder="How can we help?" className={inp} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message-input" />
          <button disabled={busy} className="px-6 py-3 bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50" data-testid="contact-submit-btn">{busy ? "Sending…" : "Send message"}</button>
        </form>
      )}
    </Shell>
  );
}

export function StatusPage() {
  const { data, loading } = useFetch("/health");
  return (
    <Shell eyebrow="System status" title={loading ? "Checking…" : data?.status === "operational" ? "All core systems operational" : "Degraded performance"}>
      {loading ? <Loading /> : (
        <div className="max-w-xl border border-gray-200 divide-y" data-testid="status-components">
          {Object.entries(data?.components || {}).map(([k, v]) => (
            <div key={k} className="flex justify-between p-4 text-sm"><span className="capitalize">{k}</span>
              <span className={`inline-flex items-center gap-1 ${v === "operational" || v === "configured" ? "text-green-700" : "text-gray-500"}`}>
                {v === "operational" || v === "configured" ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}{v.replace("_", " ")}</span></div>
          ))}
          <div className="p-4 text-xs text-gray-500">Checked {fmtTime(data?.checked_at)}</div>
        </div>
      )}
    </Shell>
  );
}
